import type { Estimate } from '@/types/estimate';
import { isMembershipSetupFeeLine } from '@notifi/shared-types/membership-line';
import { resolveEstimateLineAmounts } from '@/lib/sales/line-item-pricing';
import { reconciledTaxBreakdown, taxableSubtotalOf } from '@/lib/tax/tax-utils';
import {
  customerFacingLines,
  customerVisibleRows,
  offerCountedLines,
  rowShowsMonetaryAmount,
  soldLineSet,
  splitOptionalOffers,
  type CustomerFacingLine,
} from '@/lib/sales/service-components';

/**
 * The money the block-based estimate document renders — computed with the SAME
 * helpers the customer PDF and public quote page use, never re-derived by hand.
 *
 * The retired classic `EstimatePDF.tsx` performed this exact sequence inline.
 * The block renderer cannot import that private computation without dragging in
 * react-pdf, so the sequence is mirrored here against the shared helpers
 * (`splitOptionalOffers`, `customerFacingLines`, `customerVisibleRows`,
 * `resolveEstimateLineAmounts`, `soldLineSet`, `rowShowsMonetaryAmount`,
 * `reconciledTaxBreakdown`). Every figure is summed over the raw priced lines,
 * so the block document and the PDF report identical numbers for one estimate.
 */

export type EstimateDocRow = {
  /** The customer-facing row (rolled-up or itemized). */
  row: CustomerFacingLine<Estimate['lineItems'][number]>;
  /** A top-level optional offer the customer chooses; tags "(Optional)". */
  isOffer: boolean;
  /** Whether this optional offer is currently selected (counted in totals). */
  isSelected: boolean;
};

/** One line's resolved figures, summed across every raw line the row stands for. */
export interface EstimateDocRowAmounts {
  quantity: number;
  effectiveUnitPrice: number;
  lineSubtotal: number;
  lineTax: number;
  lineTotal: number;
  /**
   * What the row PRINTS as its amount: quantity x price, before discounts and
   * before tax. The totals block below the lines states Subtotal, then the
   * discounts, then each tax — so a row that already folded those in printed
   * lines that did not add up to the Subtotal under them ($433.00 + $649.50
   * over "Subtotal $1,000.00") and showed the customer the tax twice.
   */
  printedAmount: number;
  /** `false` for an "included in service" row that states no monetary amount. */
  showsAmount: boolean;
}

export interface EstimateDocMoney {
  /** Rows to print, in order: required first, then optional offers. */
  rows: EstimateDocRow[];
  subtotal: number;
  membershipSetupFee?: number;
  taxTotal: number;
  /** Net amount of the lines that carry tax — see `taxableSubtotalOf`. */
  taxableSubtotal: number;
  /** One row per tax jurisdiction when the frozen snapshot reconciles. */
  taxBreakdown: ReturnType<typeof reconciledTaxBreakdown>;
  discountTotal: number;
  membershipDiscountTotal: number;
  grandTotal: number;
  depositAmount: number;
}

/**
 * Resolve one customer-facing row's figures by summing `resolveEstimateLineAmounts`
 * over the raw lines it stands for — the identical derivation the classic PDF used
 * per row, so a rolled-up service reconciles to the document total.
 */
export function resolveEstimateDocRowAmounts(
  row: CustomerFacingLine<Estimate['lineItems'][number]>,
): EstimateDocRowAmounts {
  const item = row.line;
  const quantity = Number(item.quantity) || 0;
  const amounts = row.lines.map(resolveEstimateLineAmounts);
  const lineSubtotal = amounts.reduce((sum, a) => sum + a.lineSubtotal, 0);
  const lineTax = amounts.reduce((sum, a) => sum + a.lineTax, 0);
  const lineTotal = amounts.reduce((sum, a) => sum + a.lineTotal, 0);
  const effectiveUnitPrice = quantity > 0 ? lineSubtotal / quantity : Number(item.basePrice) || 0;
  return {
    quantity,
    effectiveUnitPrice,
    lineSubtotal,
    lineTax,
    lineTotal,
    printedAmount: lineSubtotal,
    showsAmount: rowShowsMonetaryAmount(row),
  };
}

/**
 * Compute the block document's money exactly as the PDF does: required lines
 * plus any SELECTED optional offer make up the priced set that the subtotal,
 * tax and (fallback) grand total sum over. The persisted `grandTotal` stays
 * authoritative; only a legacy row missing it falls back to Σ sold lineTotal.
 */
export function computeEstimateDocMoney(estimate: Estimate): EstimateDocMoney {
  // `lineItems` is typed as an array, but guard against a malformed payload so
  // the money computation (run on every render) never throws.
  const { required: requiredItems, offers: optionalOffers } = splitOptionalOffers(
    estimate.lineItems ?? [],
  );

  const requiredRows = customerVisibleRows(customerFacingLines(requiredItems));
  const optionalRows: EstimateDocRow[] = optionalOffers.flatMap((offer) => {
    const selected = Boolean(offer.parent.isSelected);
    return customerVisibleRows(customerFacingLines([offer.parent, ...offer.components])).map(
      (row) => ({
        row,
        // Only the top-level service is the customer's offer; itemized parts
        // under it render as ordinary rows.
        isOffer: row.line === offer.parent,
        isSelected: selected,
      }),
    );
  });

  const rows: EstimateDocRow[] = [
    ...requiredRows.map((row) => ({ row, isOffer: false, isSelected: true })),
    ...optionalRows,
  ];

  const selectedOptional = optionalOffers.flatMap((offer) =>
    offerCountedLines(offer, Boolean(offer.parent.isSelected)),
  );
  const pricedItems = [...requiredItems, ...selectedOptional];

  const subtotal = pricedItems.reduce(
    (sum, item) => sum + resolveEstimateLineAmounts(item).lineSubtotal,
    0,
  );
  const taxTotal = pricedItems.reduce(
    (sum, item) => sum + resolveEstimateLineAmounts(item).lineTax,
    0,
  );
  const taxBreakdown = reconciledTaxBreakdown(pricedItems, taxTotal);

  const discountTotal = Number(estimate.discountTotal) || 0;
  const membershipDiscountTotal = Number(estimate.membershipDiscountTotal) || 0;

  const hasAuthoritativeGrandTotal =
    typeof estimate.grandTotal === 'number' && Number.isFinite(estimate.grandTotal);
  const grandTotal = hasAuthoritativeGrandTotal
    ? estimate.grandTotal
    : [...soldLineSet(estimate.lineItems)].reduce(
        (sum, item) => sum + resolveEstimateLineAmounts(item).lineTotal,
        0,
      );

  return {
    rows,
    subtotal,
    membershipSetupFee: pricedItems
      .filter((item) => isMembershipSetupFeeLine(item) && item.displayConfig?.hidePrice !== true)
      .reduce((sum, item) => sum + resolveEstimateLineAmounts(item).lineSubtotal, 0),
    taxTotal,
    // With the snapshot, not just the amounts: a taxable line can be allocated
    // $0.00 of a tax rounded once per document, and it is still taxable.
    taxableSubtotal: taxableSubtotalOf(
      pricedItems.map((item) => ({
        ...resolveEstimateLineAmounts(item),
        taxSnapshot: item.taxSnapshot,
      })),
    ),
    taxBreakdown,
    discountTotal,
    membershipDiscountTotal,
    grandTotal,
    depositAmount: Number(estimate.depositAmount) || 0,
  };
}

const MONEY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

/** Group thousands the same way every other estimate surface does. */
export function fmtMoney(n: number): string {
  return MONEY.format(Number.isFinite(n) ? n : 0);
}
